import React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Typography,
  IconButton,
  FormControl,
  FormControlLabel,
  Checkbox,
} from "@mhome/ui";
import { CircularProgress, useMediaQuery } from "@mui/material";
import { FormLabel, RadioGroup, Radio, FormGroup } from "@mhome/ui";
import { Nfc as NfcIcon, Close as CloseIcon } from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import { safeAreaInsetsState } from "../global-state";
import { spacingToPx } from "../lib/utils";

const POLICY_ALL_PHONE = "ALL_PHONE";
const POLICY_CUSTOM_PHONE = "CUSTOM_PHONE";

const NfcWriteDialog = ({
  open,
  onClose,
  duplicateMode,
  duplicateTagName,
  writeMode,
  newTagName,
  writingNfc,
  newTagDescription,
  newTagPolicy,
  handleNameChange,
  nameError,
  setNewTagDescription,
  handleNewTagPolicyChange,
  trustedSources,
  newTagTrustedSources,
  handleNewTagTrustedSourceToggle,
  handleStartNfcWrite,
  countdown,
  countdownActive,
}) => {
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isSmallScreen}
      variant={isSmallScreen ? undefined : "drawer"}
      PaperProps={{
        style: {
          width: isSmallScreen ? "100%" : "600px",
          marginTop: isSmallScreen ? 0 : `${safeAreaInsets.top}px !important`,
          marginBottom: isSmallScreen
            ? 0
            : `${safeAreaInsets.bottom}px !important`,
          height: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
          maxHeight: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
          paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
          paddingBottom: isSmallScreen ? `${safeAreaInsets.bottom}px` : 0,
          paddingRight: `${safeAreaInsets.right}px`,
        },
      }}
    >
      <DialogTitle
        className="flex items-center gap-2"
        style={{
          minHeight: "auto",
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={writingNfc}
          style={{
            flexShrink: 0,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          variant="h6"
          component="div"
          style={{
            lineHeight: 1.2,
            marginBottom: 0,
          }}
        >
          {duplicateMode ? "Duplicate NFC Tag" : "Write NFC Tag"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* Dialog content update */}
        <DialogContentText style={{ marginBottom: spacingToPx(3) }}>
          {duplicateMode ? (
            writeMode ? (
              <>
                Ready to duplicate &quot;{duplicateTagName}&quot;. Please scan
                an NFC tag now. This will write same data to your new tag. Tags
                with same data work identically.
              </>
            ) : (
              <>Preparing to duplicate &quot;{duplicateTagName}&quot;...</>
            )
          ) : writeMode ? (
            <>
              Ready to write NFC tag &quot;{newTagName}&quot;. Please scan an
              NFC tag now.
            </>
          ) : (
            <>Enter the details for your new NFC tag:</>
          )}
        </DialogContentText>

        {!writeMode && !duplicateMode && (
          <>
            <TextField
              autoFocus
              required
              margin="dense"
              label="Name"
              fullWidth
              variant="outlined"
              value={newTagName}
              onChange={(e) => handleNameChange(e.target.value)}
              error={!!nameError}
              helperText={nameError || "Required - Name for your NFC tag"}
              style={{ marginBottom: spacingToPx(2) }}
            />

            <TextField
              margin="dense"
              label="Description"
              fullWidth
              variant="outlined"
              multiline
              rows={2}
              value={newTagDescription}
              onChange={(e) => setNewTagDescription(e.target.value)}
              style={{ marginBottom: spacingToPx(3) }}
              helperText="Optional - Additional description"
            />

            {/* Policy Selection for New Tag */}
            <FormControl
              component="fieldset"
              style={{ marginBottom: spacingToPx(3) }}
            >
              <FormLabel
                component="legend"
                style={{ fontSize: "0.875rem", fontWeight: 500 }}
              >
                Access Policy
              </FormLabel>
              <RadioGroup
                value={newTagPolicy}
                onChange={(e) => handleNewTagPolicyChange(e.target.value)}
                style={{ marginTop: spacingToPx(1) }}
              >
                <FormControlLabel
                  value={POLICY_ALL_PHONE}
                  control={<Radio size="small" />}
                  label="All Phones"
                  style={{ height: 32 }}
                />
                <FormControlLabel
                  value={POLICY_CUSTOM_PHONE}
                  control={<Radio size="small" />}
                  label="Custom Phones"
                  style={{ height: 32 }}
                />
              </RadioGroup>
              <Typography
                variant="caption"
                className="text-muted-foreground"
                style={{
                  marginTop: spacingToPx(0.5),
                  display: "block",
                  fontSize: "0.75rem",
                }}
              >
                {newTagPolicy === POLICY_ALL_PHONE
                  ? "Any phone can scan this tag"
                  : "Only selected phones can scan this tag"}
              </Typography>
            </FormControl>

            {/* Trusted Sources Selection for New Tag */}
            {newTagPolicy === POLICY_CUSTOM_PHONE && (
              <FormControl
                component="fieldset"
                style={{ marginBottom: spacingToPx(3) }}
              >
                <FormLabel
                  component="legend"
                  style={{ fontSize: "0.875rem", fontWeight: 500 }}
                >
                  Select Trusted Phones
                </FormLabel>
                {trustedSources.length === 0 ? (
                  <Typography
                    variant="body2"
                    className="text-muted-foreground"
                    style={{
                      marginTop: spacingToPx(1),
                    }}
                  >
                    No phones available. Make sure phones are registered in your
                    system.
                  </Typography>
                ) : (
                  <FormGroup style={{ marginTop: spacingToPx(1) }}>
                    {trustedSources.map((source) => (
                      <FormControlLabel
                        key={source.sourceId}
                        control={
                          <Checkbox
                            size="small"
                            checked={newTagTrustedSources.some(
                              (ts) => ts.sourceId === source.sourceId
                            )}
                            onChange={() =>
                              handleNewTagTrustedSourceToggle(source.sourceId)
                            }
                          />
                        }
                        label={source.sourceName}
                        style={{ height: 32 }}
                      />
                    ))}
                  </FormGroup>
                )}
                {newTagPolicy === POLICY_CUSTOM_PHONE &&
                  newTagTrustedSources.length === 0 && (
                    <Typography
                      variant="caption"
                      className="text-destructive"
                      style={{
                        marginTop: spacingToPx(1),
                      }}
                    >
                      Please select at least one phone for custom policy
                    </Typography>
                  )}
              </FormControl>
            )}
          </>
        )}

        {duplicateMode && !writeMode && (
          <>
            <DialogContentText style={{ marginBottom: spacingToPx(2) }}>
              Entering duplication mode...
            </DialogContentText>
            <ol style={{ paddingLeft: spacingToPx(2) }}>
              <Typography
                component="li"
                style={{ marginBottom: spacingToPx(1) }}
              >
                Make sure NFC is enabled on your device
              </Typography>
              <Typography component="li">
                You will be prompted to scan an NFC tag momentarily
              </Typography>
            </ol>
          </>
        )}

        {writeMode && (
          <>
            <DialogContentText style={{ marginBottom: spacingToPx(2) }}>
              {duplicateMode ? "Duplication" : "Write"} mode is active. Follow
              these steps:
            </DialogContentText>
            <ol style={{ paddingLeft: spacingToPx(2) }}>
              <Typography
                component="li"
                style={{ marginBottom: spacingToPx(1) }}
              >
                Place an NFC tag close to your device (usually on the back)
              </Typography>
              <Typography
                component="li"
                style={{ marginBottom: spacingToPx(1) }}
              >
                Keep the tag in position until you hear a sound or see a
                notification
              </Typography>
              <Typography component="li">
                The tag will be automatically{" "}
                {duplicateMode ? "duplicated" : "written"} and this dialog will
                close
              </Typography>
            </ol>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: spacingToPx(2),
                padding: spacingToPx(2),
                backgroundColor: "hsl(var(--primary))",
                color: "hsl(var(--primary-foreground))",
                borderRadius: "var(--radius)",
              }}
            >
              <NfcIcon style={{ marginRight: spacingToPx(2), fontSize: 24 }} />
              <div style={{ flex: 1 }}>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  NFC Write Mode Active - Scan your NFC tag now
                </Typography>
                {countdownActive && (
                  <Typography
                    variant="caption"
                    style={{ display: "block", marginTop: spacingToPx(0.5) }}
                  >
                    Time remaining: {Math.floor(countdown / 60)}:
                    {(countdown % 60).toString().padStart(2, "0")}
                  </Typography>
                )}
              </div>
            </div>
          </>
        )}

        {writingNfc && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginTop: spacingToPx(2),
              padding: spacingToPx(2),
              backgroundColor: "hsl(var(--card))",
              borderRadius: "var(--radius)",
            }}
          >
            <CircularProgress
              size={20}
              style={{ marginRight: spacingToPx(2) }}
            />
            <Typography variant="body2">Entering write mode...</Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={writingNfc}>
          {writeMode
            ? duplicateMode
              ? "Cancel NFC Duplication"
              : "Cancel NFC Write"
            : "Cancel"}
        </Button>
        {!writeMode && !duplicateMode && (
          <Button
            onClick={handleStartNfcWrite}
            variant="contained"
            disabled={
              writingNfc ||
              !newTagName.trim() ||
              (newTagPolicy === POLICY_CUSTOM_PHONE &&
                newTagTrustedSources.length === 0)
            }
            startIcon={
              writingNfc ? <CircularProgress size={16} /> : <NfcIcon />
            }
          >
            {writingNfc ? "Starting..." : "Next"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NfcWriteDialog;
